/**
 * Workspace shell configuration (tabs, toolbar, header copy).
 * Tab `path` values are top-level routes registered in the router.
 */

export const shellHeader = {
  brandMark: 'HR',
  title: 'HR Workspace',
  subtitle: 'CRC Transport'
};

export const shellToolbarActions = [
  {
    key: 'columnVisibility',
    label: 'Column Visibility',
    icon: 'pi-list',
    hasMenu: true,
    menuType: 'column',
    tabs: ['table'],
  },
  {
    key: 'export',
    label: 'Export',
    icon: 'pi-download',
    hasMenu: true,
    tabs: ['tree', 'table', 'departments', 'positions'],
  },
  {
    key: 'importEmployees',
    label: 'Import',
    icon: 'pi-upload',
    tabs: ['table'],
  },
  {
    key: 'addEmployee',
    label: 'Add Employee',
    icon: 'pi-user-plus',
    entity: 'employees',
    tabs: ['table'],
  },
  {
    key: 'addDepartment',
    label: 'Add Department',
    icon: 'pi-plus',
    entity: 'departments',
    tabs: ['departments'],
  },
  {
    key: 'addPosition',
    label: 'Add Position',
    icon: 'pi-plus',
    entity: 'positions',
    tabs: ['positions'],
  },
];

export const inboxTabPermissions = [
  'dashboard.view.global',
  'dashboard.view.department',
  'employees.view.global',
  'employees.view.department',
  'time_off.request',
  'time_off.approve_supervisor',
  'time_off.approve_hr'
];

const employeesViewPermissions = ['employees.view.global', 'employees.view.department'];

export const shellTabs = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: 'pi-chart-bar',
    path: '/dashboard',
    routeName: 'dashboard',
    requiredAnyPermission: [
      'dashboard.view.global',
      'dashboard.view.department',
      'employees.view.global',
      'employees.view.department'
    ]
  },
  {
    key: 'tree',
    label: 'Org Tree',
    icon: 'pi-sitemap',
    path: '/tree',
    routeName: 'tree',
    requiredAnyPermission: ['org.view']
  },
  {
    key: 'table',
    label: 'Employees',
    icon: 'pi-table',
    path: '/employees',
    routeName: 'employees',
    relatedRouteNames: [
      'employee-detail',
      'employee-edit',
      'employee-create',
      'plan-editor',
      'appraisal-editor',
      'employee-profile-report'
    ],
    requiredAnyPermission: employeesViewPermissions
  },
  {
    key: 'directory',
    label: 'Directory',
    icon: 'pi-id-card',
    path: '/directory',
    routeName: 'directory',
    requiredAnyPermission: employeesViewPermissions
  },
  {
    key: 'compensation',
    label: 'Compensation',
    icon: 'pi-wallet',
    path: '/compensation',
    routeName: 'compensation',
    requiredAnyPermission: [
      'compensation.view_all',
      'compensation.view_us',
      'compensation.view'
    ],
    strictPermissions: true
  },
  {
    key: 'attendance',
    label: 'Attendance',
    icon: 'pi-calendar',
    path: '/attendance',
    routeName: 'attendance',
    requiredAnyPermission: ['time_off.view_all', 'time_off.view_department']
  },
  {
    key: 'performance',
    label: 'Performance',
    icon: 'pi-star',
    path: '/performance',
    routeName: 'performance',
    isPlaceholder: true,
    hidden: true
  },
  {
    key: 'my-profile',
    label: 'My Profile',
    icon: 'pi-user',
    path: '/my-profile',
    routeName: 'my-profile',
    visibleForRegularUser: true
  },
  {
    key: 'inbox',
    label: 'Inbox',
    icon: 'pi-inbox',
    path: '/inbox',
    routeName: 'inbox',
    requiredAnyPermission: inboxTabPermissions,
    visibleForRegularUser: true
  },
  {
    key: 'past',
    label: 'Past Employees',
    icon: 'pi-folder',
    path: '/past',
    routeName: 'past',
    requiredAnyPermission: employeesViewPermissions
  },
  {
    key: 'kids',
    label: 'Kids',
    icon: 'pi-heart',
    path: '/kids',
    routeName: 'kids',
    isPlaceholder: true,
    hidden: true
  },
  {
    key: 'calendar',
    label: 'Calendar',
    icon: 'pi-calendar-clock',
    path: '/calendar',
    routeName: 'calendar',
    visibleForRegularUser: true
  },
  {
    key: 'candidates',
    label: 'Candidates',
    icon: 'pi-users',
    path: '/candidates',
    routeName: 'candidates',
    relatedRouteNames: ['candidate-detail'],
    requiredAnyPermission: ['candidates.view.global', 'candidates.view_deleted.global']
  },
  {
    key: 'documents',
    label: 'Documents',
    icon: 'pi-folder-open',
    path: '/documents',
    routeName: 'documents',
    requiredAnyPermission: ['documents.catalog.view']
  },
  {
    key: 'reports',
    label: 'Payroll Reports',
    icon: 'pi-file',
    path: '/reports',
    routeName: 'reports',
    requiredAnyPermission: ['payroll_reports.view'],
    strictPermissions: true
  }
];

export const getShellTabByKey = (key) => shellTabs.find((tab) => tab.key === key) || null;

/**
 * Sidebar IA — section-first layout (nav-source only).
 * Registry metadata lives in shellTabs / shellAdminTabs.
 */
export const shellNavSections = [
  {
    id: 'workspace',
    label: 'Workspace',
    items: [{ type: 'tab', tabKey: 'dashboard' }]
  },
  {
    id: 'people',
    label: 'People',
    items: [
      { type: 'tab', tabKey: 'directory' },
      { type: 'tab', tabKey: 'table' },
      { type: 'tab', tabKey: 'past' },
      { type: 'tab', tabKey: 'tree' }
    ]
  },
  {
    id: 'hr-operations',
    label: 'HR operations',
    items: [
      { type: 'tab', tabKey: 'attendance' },
      { type: 'tab', tabKey: 'compensation' },
      { type: 'tab', tabKey: 'candidates' },
      { type: 'tab', tabKey: 'reports' },
      { type: 'adminTab', tabKey: 'employee-reports' },
      { type: 'tab', tabKey: 'documents' }
    ]
  },
  {
    id: 'communication',
    label: 'Communication',
    items: [
      { type: 'tab', tabKey: 'my-profile' },
      { type: 'tab', tabKey: 'calendar' },
      { type: 'tab', tabKey: 'inbox' }
    ]
  },
  {
    id: 'administration',
    label: 'Administration',
    items: [
      { type: 'adminTab', tabKey: 'departments' },
      { type: 'adminTab', tabKey: 'positions' },
      { type: 'adminTab', tabKey: 'plan-coverage' }
    ]
  }
];

/** Admin sidebar group — nav-source only (routes, labels, permissions). */
export const shellAdminTabs = [
  {
    key: 'plan-coverage',
    label: 'Plan Coverage',
    path: '/plan-coverage',
    icon: 'chart-pie',
    permissionAny: ['dashboard.view.global', 'dashboard.view.department']
  },
  {
    key: 'departments',
    label: 'Departments',
    path: '/departments',
    icon: 'building',
    permissionAny: [
      'departments.view.global',
      'departments.view.department',
      'departments.manage.global'
    ]
  },
  {
    key: 'positions',
    label: 'Positions',
    path: '/positions',
    icon: 'briefcase',
    permissionAny: [
      'positions.view.global',
      'positions.view.department'
    ]
  },
  {
    key: 'employee-reports',
    label: 'Employee Reports',
    path: '/employee-reports',
    icon: 'file-edit',
    permissionAny: [
      'employee_reports.view.global',
      'employee_reports.view.department'
    ]
  },
  {
    key: 'all-workflows',
    label: 'All Workflows',
    path: '/all-workflows',
    icon: 'sitemap',
    permissionAny: [
      'dashboard.view.global',
      'dashboard.view.department',
      'employees.view.global',
      'employees.view.department'
    ],
    hidden: true
  }
];

export const getShellAdminTabByKey = (key) =>
  shellAdminTabs.find((tab) => tab.key === key) || null;
